import { useEffect, type ReactNode } from 'react';
import { ThemeProvider } from '@mui/material';
import { useAppSelector } from './store/hooks';
import { lightTheme, darkTheme } from './styles/theme';
import OfflineBanner from './components/common/OfflineBanner';
import ServiceWorkerUpdate from './components/pwa/ServiceWorkerUpdate';

interface AppProps {
  children: ReactNode;
}

/**
 * Root application wrapper component
 * 
 * Provides the theme context for the entire application including:
 * - Dynamic theme switching based on Redux state
 * - Global offline status banner
 * - Service worker update notifications
 * - CSS custom properties synchronization
 * 
 * Theme Integration:
 * - Reads current theme from Redux store (initialized from localStorage in uiSlice)
 * - Applies data-theme attribute to document root for CSS variable switching
 * - Wraps application with MUI ThemeProvider for dynamic theme switching
 * - Theme changes are persisted to localStorage via Redux actions
 */
function App({ children }: AppProps) {
  const currentTheme = useAppSelector((state) => state.ui.theme);

  /**
   * Apply data-theme attribute to document root whenever theme changes
   * This enables CSS custom properties to switch between light and dark values
   */
  useEffect(() => {
    console.log('App: Setting data-theme attribute to:', currentTheme);
    document.documentElement.setAttribute('data-theme', currentTheme);
  }, [currentTheme]);

  /**
   * Select appropriate MUI theme based on current theme mode
   */
  const muiTheme = currentTheme === 'dark' ? darkTheme : lightTheme;

  console.log('App: Current theme from Redux:', currentTheme);
  console.log('App: Selected MUI theme mode:', muiTheme.palette.mode);

  return (
    <ThemeProvider theme={muiTheme}>
      {/* Global offline status indicator */}
      <OfflineBanner />
      
      {/* Service worker update prompt */}
      <ServiceWorkerUpdate />
      
      {/* Router and all route content */}
      {children}
    </ThemeProvider>
  );
}

export default App;